/**
 * User flow tracking via sessionStorage.
 *
 * Cleared automatically when the tab closes; persists across same-tab
 * client-side navigations (unlike React context which resets on hard refresh).
 *
 * Values:
 *   'onboarding'             — fresh registration with no specific job attached
 *   'onboarding_application' — new user who registered in order to apply to a job
 *   'legacy_application'     — returning (already-authenticated) user applying to a job
 *
 * Only 'onboarding' and 'onboarding_application' users should be defaulted to
 * a public profile. Legacy users' existing preferences must be respected.
 */

const FLOW_KEY = 'dp_user_flow';

export type UserFlowValue =
  | 'onboarding'
  | 'onboarding_application'
  | 'legacy_application';

export function getFlow(): UserFlowValue | null {
  try {
    return (sessionStorage.getItem(FLOW_KEY) as UserFlowValue) ?? null;
  } catch {
    return null;
  }
}

export function setFlow(flow: UserFlowValue): void {
  try {
    sessionStorage.setItem(FLOW_KEY, flow);
  } catch {}
}

export function clearFlow(): void {
  try {
    sessionStorage.removeItem(FLOW_KEY);
  } catch {}
}

/**
 * Returns true when the current flow represents a brand-new user
 * (onboarding-only or first application right after registration).
 * These users should default to public profile visibility.
 */
export function isNewUserFlow(): boolean {
  const flow = getFlow();
  return flow === 'onboarding' || flow === 'onboarding_application';
}
